import '@apps/web/features/phoebe_chat_v3/ui/working_row.css';

import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useReducer, useState } from 'react';

import { PhoebeLoader } from '@apps/web/components/ui/phoebe_loader';
import { type V3ActivityStatus } from '@apps/web/features/phoebe_chat_v3/model/activity_presentation';
import { type V3ReasoningMilestoneStep } from '@apps/web/features/phoebe_chat_v3/model/transcript';
import { isFallbackV3WorkingStatus } from '@apps/web/features/phoebe_chat_v3/model/working_status';
import {
    advanceV3WorkingTrace,
    createV3WorkingTrace,
    retainLastV3WorkingStatus,
    statusMatchesV3WorkingTraceStep,
} from '@apps/web/features/phoebe_chat_v3/model/working_trace';
import { cn } from '@apps/web/lib/utils';

const WORKING_TRACE_ROW_HEIGHT_PX = 28;

export function V3WorkingRow({
    status: rawStatus,
    completedSteps,
}: {
    readonly status: V3ActivityStatus;
    readonly completedSteps: ReadonlyArray<V3ReasoningMilestoneStep>;
}) {
    const [expanded, setExpanded] = useState(false);
    const [trace, advanceTrace] = useReducer(
        advanceV3WorkingTrace,
        { status: rawStatus, completedSteps },
        ({ status, completedSteps: initialSteps }) =>
            createV3WorkingTrace(status, initialSteps),
    );
    const status = retainLastV3WorkingStatus(trace, rawStatus);
    const statusKind = status.kind;
    const statusLabel = status.label;
    const statusDetail = status.detail;

    // Reconcile after paint so the active dot animates from its old row to
    // the new one instead of appearing at the final position immediately.
    useEffect(() => {
        advanceTrace({
            status: {
                kind: statusKind,
                label: statusLabel,
                detail: statusDetail,
            },
            completedSteps,
        });
    }, [completedSteps, statusDetail, statusKind, statusLabel]);

    const isReplying = status.kind === 'replying';
    useEffect(() => {
        if (isReplying) {
            setExpanded(false);
        }
    }, [isReplying]);

    const lastStep = expanded ? trace.steps[trace.steps.length - 1] : undefined;
    const visibleSteps = !expanded
        ? []
        : lastStep && statusMatchesV3WorkingTraceStep(status, lastStep)
          ? trace.steps.slice(0, -1)
          : trace.steps;
    const activeDotOffsetPx = visibleSteps.length * WORKING_TRACE_ROW_HEIGHT_PX;
    const hasTraceContent =
        trace.steps.length > 0 || !isFallbackV3WorkingStatus(status);

    if (!hasTraceContent) {
        return (
            <div
                role="status"
                aria-live="polite"
                className="max-w-[700px] text-sm leading-5 text-[#244045]"
            >
                <div className="flex h-7 items-center gap-2.5 font-semibold text-[#002429]">
                    <PhoebeLoader className="shrink-0" />
                    <span className="v3-working-header-label min-w-0 truncate">
                        {status.label}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className="max-w-[700px] text-sm leading-5 text-[#244045]"
        >
            <button
                type="button"
                aria-expanded={expanded}
                data-testid="v3-working-toggle"
                onClick={() => setExpanded((value) => !value)}
                className="group flex h-7 max-w-full cursor-pointer items-center gap-2.5 font-semibold text-[#002429]"
            >
                <PhoebeLoader className="shrink-0" />
                <span
                    key={expanded ? 'expanded-heading' : status.label}
                    className="v3-working-header-label min-w-0 truncate"
                >
                    {expanded ? 'Working' : status.label}
                </span>
                <ChevronDown
                    aria-hidden="true"
                    className={cn(
                        'size-3.5 shrink-0 text-[#879899] transition-transform duration-200 group-hover:text-[#002429] motion-reduce:transition-none',
                        expanded && 'rotate-180',
                    )}
                />
            </button>
            <div
                aria-hidden={!expanded}
                data-testid="v3-working-trace"
                className={cn(
                    'grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
                    expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
            >
                {expanded ? (
                    <div className="min-h-0 overflow-hidden">
                        <div
                            className={cn(
                                'relative mt-0.5 transition-opacity duration-200 motion-reduce:transition-none',
                                expanded ? 'opacity-100' : 'opacity-0',
                            )}
                        >
                            {visibleSteps.map((step) => (
                                <div
                                    key={step.id}
                                    data-slot="v3-working-completed-step"
                                    data-testid="v3-working-completed-step"
                                    className="grid h-7 grid-cols-[16px_minmax(0,1fr)] items-center gap-2.5 text-[#617578]"
                                >
                                    <span
                                        aria-hidden="true"
                                        className="relative flex size-4 items-center justify-center"
                                    >
                                        <Check
                                            data-testid="v3-working-step-check"
                                            strokeWidth={2.5}
                                            className="v3-working-step-check size-3 shrink-0 text-[#5533EE]"
                                        />
                                        <span className="v3-working-connector absolute top-[14px] h-4 w-px origin-top bg-[#DBE3E3]" />
                                    </span>
                                    <span className="flex min-w-0 items-baseline gap-2">
                                        <span className="truncate">
                                            {step.label}
                                        </span>
                                        {step.detail ? (
                                            <span className="min-w-0 truncate text-xs text-[#879899]">
                                                {step.detail}
                                            </span>
                                        ) : null}
                                    </span>
                                </div>
                            ))}
                            <div className="grid h-7 grid-cols-[16px_minmax(0,1fr)] items-center gap-2.5 text-[#002429]">
                                <span aria-hidden="true" className="size-4" />
                                <span className="flex min-w-0 items-baseline gap-2">
                                    <span className="truncate">
                                        {status.label}
                                    </span>
                                    {status.detail ? (
                                        <span className="min-w-0 truncate text-xs text-[#617578]">
                                            {status.detail}
                                        </span>
                                    ) : null}
                                </span>
                            </div>
                            <span
                                aria-hidden="true"
                                data-slot="v3-working-active-dot"
                                data-testid="v3-working-active-dot"
                                className="v3-working-active-dot absolute top-1.5 left-0 flex size-4 items-center justify-center"
                                style={{
                                    transform: `translateY(${activeDotOffsetPx}px)`,
                                }}
                            >
                                <span className="v3-working-active-dot-core size-2 rounded-full bg-[#5533EE]" />
                            </span>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
